import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Shield, Loader2, Key, CheckCircle, AlertTriangle, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { motion } from 'motion/react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

interface VerificationData {
  name: string;
  email: string;
  phone: string;
}

export default function VerifyProspectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifiedData, setVerifiedData] = useState<VerificationData | null>(null);

  // Password Form State
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token || !emailParam) {
      setError('Invalid verification query parameters. Missing secure signature token.');
      setLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch('/api/verify-prospect-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email: emailParam })
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Identity proof verification failed.');
        }

        const data = await response.json();
        setVerifiedData(data.verificationData);
      } catch (err: any) {
        setError(err.message || 'Signature token validation failed.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, emailParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!verifiedData) return;
    if (password.length < 6) {
      setSubmitError('Password must be at least 6 characters in length.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Password and verification entries do not match.');
      return;
    }
    if (!waiverAccepted) {
      setSubmitError('You must acknowledge and accept the digital waiver terms to continue.');
      return;
    }

    setSubmitting(true);

    try {
      // 1. Register Auth account client-side
      const userCredential = await createUserWithEmailAndPassword(auth, verifiedData.email, password);
      const uid = userCredential.user.uid;

      // 2. Provision Firestore profile record
      await setDoc(doc(db, 'users', uid), {
        uid,
        role: 'member',
        membershipTier: 'Prospect',
        membershipStatus: 'active',
        email: verifiedData.email,
        displayName: verifiedData.name,
        phone: verifiedData.phone,
        joinedAt: new Date().toISOString(),
        waiverSigned: true,
        automationTags: []
      }, { merge: true });

      // 3. Mark verification token as consumed/verified in backend
      try {
        await fetch('/api/consume-prospect-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email: verifiedData.email })
        });
      } catch (consumeErr) {
        console.error('Failed to consume token:', consumeErr);
      }

      // 4. Send welcome email notification via custom SMTP service
      try {
        await fetch('/api/send-prospect-welcome', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: verifiedData.email,
            displayName: verifiedData.name
          })
        });
      } catch (emailErr) {
        console.error('Failed to send SMTP welcome email:', emailErr);
      }

      // Route happily to dashboard
      navigate('/member-portal');
    } catch (err: any) {
      console.error('Registration processing failure:', err);
      if (err.code === 'auth/email-already-in-use') {
        setSubmitError('This email is already in use by another active account.');
      } else {
        setSubmitError(err.message || 'Failed to complete credentials enrollment.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ows-dark text-white pt-32 pb-20 flex flex-col items-center justify-center px-4 relative overflow-hidden" id="ows-verify-prospect-viewport">
      {/* Visual background ambient details */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-ows-teal/5 blur-[100px] pointer-events-none rounded-full" />

      <div className="w-full max-w-lg relative z-10">
        <div className="text-center mb-12">
          <p className="text-[10px] font-mono tracking-[0.3em] text-ows-teal uppercase mb-3">Syndicate Register Access</p>
          <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest leading-none">
            PROSPECT <span className="text-ows-teal font-normal font-sans">ACTIVATION</span>
          </h1>
        </div>

        {loading ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-zinc-900/60 backdrop-blur-md rounded-[2.5rem] border border-white/5 p-12 text-center"
          >
            <Loader2 className="h-10 w-10 text-ows-teal animate-spin mx-auto mb-6" />
            <p className="text-xs font-mono text-white/60 uppercase tracking-widest">Validating Registry Security Tokens...</p>
          </motion.div>
        ) : error ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/80 backdrop-blur-md rounded-[2.5rem] border border-red-500/20 p-12 text-center shadow-2xl"
          >
            <div className="w-16 h-16 bg-red-500/15 text-red-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-display font-light uppercase tracking-widest mb-4">Verification Signature Failure</h2>
            <p className="text-sm text-white/70 font-light mb-8 max-w-sm mx-auto leading-relaxed">
              {error}
            </p>
            <div className="flex flex-col gap-4">
              <Link 
                to="/membership" 
                className="w-full py-4 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold text-[10px] tracking-[0.2em] uppercase transition-colors border border-white/10 text-center flex items-center justify-center gap-2"
              >
                Inquire For Access Again <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link 
                to="/" 
                className="text-[10px] font-mono tracking-widest text-ows-teal hover:underline text-center uppercase"
              >
                Return to Core Terminal
              </Link>
            </div>
          </motion.div>
        ) : verifiedData && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-zinc-900/80 backdrop-blur-xl rounded-[2.5rem] border border-white/5 p-8 md:p-12 shadow-2xl"
          >
            {/* Header info */}
            <div className="flex items-center gap-4 border-b border-white/5 pb-6 mb-8">
              <div className="w-12 h-12 bg-ows-teal/10 text-ows-teal rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[9px] font-mono tracking-widest text-white/40 uppercase">AUTHENTICITY SECURED</p>
                <h3 className="text-base font-light text-white uppercase">{verifiedData.name}</h3>
                <p className="text-[10px] font-mono text-ows-teal">{verifiedData.email}</p>
              </div>
            </div>

            {submitError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-mono leading-relaxed flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Password */}
              <div>
                <label className="block text-[9px] font-mono uppercase tracking-[0.25em] text-white/50 mb-2">Create Passkey</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">
                    <Key className="h-4 w-4" />
                  </span>
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="MINIMUM 6 CHARACTERS"
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-12 pr-12 text-sm text-white placeholder-white/30 focus:outline-none focus:border-ows-teal focus:ring-1 focus:ring-ows-teal transition-all"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-[9px] font-mono uppercase tracking-[0.25em] text-white/50 mb-2">Confirm Passkey</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">
                    <Key className="h-4 w-4" />
                  </span>
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="RE-ENTER PASSKEY TO VERIFY"
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-sm text-white placeholder-white/30 focus:outline-none focus:border-ows-teal focus:ring-1 focus:ring-ows-teal transition-all"
                  />
                </div>
              </div>

              {/* Digital Waiver & Rules */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-5 space-y-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-ows-teal">Syndicate Acknowledgment Waiver</p>
                <div className="h-32 overflow-y-auto pr-2 text-[10px] text-white/60 leading-relaxed font-sans space-y-3 scrollbar-thin">
                  <p>
                    1. SAFETY PROTOCOLS: As a Prospect tier operator of the Open Water Syndicate, I agree to abide by all harbor rules and active vessel commander safety mandates.
                  </p>
                  <p>
                    2. LIABILITY WAIVER: I release the Syndicate, its fleet vessels, and harbor captains from any liability regarding loss, injuries, or digital system anomalies incurred during expeditions or coaching sessions.
                  </p>
                  <p>
                    3. ACCOUNT CONFIDENTIALITY: I understand that my access passkey is strictly individual and cannot be shared. Access abuse will result in instant registry revocation.
                  </p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer text-xs text-white/70 select-none pt-2 border-t border-white/5">
                  <input 
                    type="checkbox"
                    checked={waiverAccepted}
                    onChange={(e) => setWaiverAccepted(e.target.checked)}
                    className="mt-0.5 select-none rounded bg-black border border-white/20 text-ows-teal focus:ring-0 focus:ring-offset-0 h-4 w-4 rounded-md cursor-pointer"
                  />
                  <span>I accept the liability waiver terms and consent to digital registry.</span>
                </label>
              </div>

              {/* Submit button */}
              <button 
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-ows-teal text-black hover:bg-ows-teal/90 rounded-xl font-bold text-[10px] tracking-[0.3em] uppercase transition-all flex items-center justify-center gap-3 shadow-lg disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    ACTIVATE MEMBER REGISTRY <Shield className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
